import React, { useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, Chip, CircularProgress,
  Alert, Divider, LinearProgress, Stack, Tabs, Tab,

} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useHealthCoverageAnalysis } from "../../hooks/insurance_data/useInsuranceAnalysis";
import {
  buildHealthCoverageLayerGroups,
  formatDuplicateRisk,
  formatCoverageItemLabel,
  subCoverageIcon,
  subCoverageTextColor,
  CATEGORY_LABELS,
} from "../../utils/healthCoverageAnalysisUtil";
import {
  HealthCoverageAnalysis,
  HealthCoverageUIRow,
  HealthCoverageLayerGroup,
  DuplicateWarning,
  SubCoverage,
} from "../../interfaces/DTO/insurance_data/HealthCoverageAnalysis";
import { Contact } from "../../interfaces/DTO/crm/contact";
import { formatHandlerCode, formatCurrency } from "../../utils/namesFormater";
import { formatPlanClassification } from "../../utils/insuranceUtil";
import { PlanClassification } from "../../interfaces/DTO/insurance_data/InsuranceData";
import HealthInsuranceCompareTable from "./HealthInsuranceCompareTable";

interface Props {
  open: boolean;
  onClose: () => void;
  contact: Contact;
  reportedPeriod: string;
}


const HealthCoverageAnalysisDialog: React.FC<Props> = ({ open, onClose, contact, reportedPeriod }) => {
  const { data, isLoading, isError, error } = useHealthCoverageAnalysis(contact.id, open);
  const [tab, setTab] = useState(1);

  const layerGroups = data ? buildHealthCoverageLayerGroups(data) : [];
  const summary = data?.summary;
  const score = summary?.critical_coverage_score ?? 0;
  const contactName = `${contact.first_name ?? ""} ${contact.last_name ?? ""}`.trim();
  const hasMissingCore = (summary?.missing_critical_categories?.length ?? 0) > 0;
  const hasDuplicates = (summary?.duplicate_warnings?.length ?? 0) > 0;

  return (
    <Dialog
      open={open} onClose={onClose} maxWidth="lg" fullWidth dir="rtl"
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6">ניתוח כיסויי בריאות — {contactName}</Typography>
          <Typography variant="body2" color="text.secondary">תקופה: {reportedPeriod}</Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {isLoading && (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, py: 6 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">מנתח כיסויים...</Typography>
          </Box>
        )}
        {isError && (
          <Alert severity="error" sx={{ m: 2 }}>
            שגיאה: {(error as any)?.message ?? "שגיאה לא ידועה"}
          </Alert>
        )}

        {data && (
          <>
            <Tabs
              value={tab} onChange={(_, v) => setTab(v)}
              sx={{ borderBottom: "1px solid", borderColor: "divider", px: 2 }}
            >
              <Tab label="פירוט כיסויים" />
              <Tab label="השוואת כיסויים" />
            </Tabs>

            <Box sx={{ p: 2 }}>
              {tab === 0 && (
                <DetailTab
                  data={data} layerGroups={layerGroups} score={score}
                  hasMissingCore={hasMissingCore} hasDuplicates={hasDuplicates}
                  summary={summary}
                />
              )}
              {tab === 1 && (
                <HealthInsuranceCompareTable data={data} layerGroups={layerGroups} />
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined">סגור</Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── DetailTab ────────────────────────────────────────────────────────────────

interface DetailTabProps {
  data: HealthCoverageAnalysis;
  layerGroups: HealthCoverageLayerGroup[];
  score: number;
  hasMissingCore: boolean;
  hasDuplicates: boolean;
  summary: HealthCoverageAnalysis["summary"] | undefined;
}

const DetailTab: React.FC<DetailTabProps> = ({ data, layerGroups, score, hasMissingCore, hasDuplicates, summary }) => (
  <Stack spacing={2.5}>

    {/* ציון ליבה */}
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
        <Box>
          <Typography variant="subtitle2" component="span">ציון ליבה רפואית </Typography>
          <Typography variant="subtitle2" component="span" fontWeight="bold">{score}%</Typography>
        </Box>
        {score === 100 && !hasMissingCore && (
          <Typography variant="caption" color="text.secondary">ליבה מלאה — בדוק שירותים והרחבות</Typography>
        )}
      </Box>
      <LinearProgress
        variant="determinate" value={score}
        color={score >= 100 ? "success" : score >= 67 ? "warning" : "error"}
        sx={{ height: 8, borderRadius: 4 }}
      />
    </Box>

    {/* חבילה לא מפורטת */}
    {summary?.has_unknown_package && (
      <Alert severity="info" icon={false} sx={{ py: 0.75 }}>
        <Typography variant="body2" gutterBottom>
          ❓ קיימת <strong>חבילת כיסויים לא מפורטת</strong> — לא ניתן לקבוע בוודאות מה כלול בה
        </Typography>
        {(summary.unknown_package_policies?.length ?? 0) > 0 && (
          <Stack spacing={0.25} sx={{ mt: 0.5 }}>
            {summary.unknown_package_policies.map((p, i) => (
              <Typography key={i} variant="caption" color="text.secondary">
                פוליסה <span dir="ltr">{p.policy_number || "—"}</span>
                {p.plan_classification ? ` ${formatPlanClassification(p.plan_classification as PlanClassification)}` : ""}
                {p.handler_code ? ` | ${formatHandlerCode(p.handler_code)}` : ""}
              </Typography>
            ))}
          </Stack>
        )}
      </Alert>
    )}

    {hasMissingCore && (
      <Alert severity="error" icon={false} sx={{ py: 0.5 }}>
        <Typography variant="body2">
          ❌ חסרים בליבה: <strong>{summary!.missing_critical_categories.map((c) => CATEGORY_LABELS[c as keyof typeof CATEGORY_LABELS] ?? c).join(", ")}</strong>
        </Typography>
      </Alert>
    )}

    {layerGroups.map((group) => <LayerSection key={group.layer} group={group} />)}

    {/* כיסויים לא מסווגים */}
    {(data.unclassified_items?.length ?? 0) > 0 && (
      <Box>
        <Divider sx={{ mb: 1.5 }} />
        <Box sx={{ bgcolor: "#fffde7", border: "1px solid #ffe082", borderRadius: 1, p: 1.25 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.75 }}>
            <Typography sx={{ fontSize: 15 }}>❔</Typography>
            <Typography variant="subtitle2" fontWeight="bold" color="warning.dark">כיסויים לא מסווגים</Typography>
          </Box>
          <Stack spacing={0.5}>
            {data.unclassified_items.map((item, i) => (
              <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Typography variant="caption" color="text.primary" fontWeight={500}>
                  {formatCoverageItemLabel(item.raw_value) || item.raw_value}
                </Typography>
                {item.policy_number && (
                  <Typography variant="caption" color="text.disabled" dir="ltr">
                    {item.policy_number}
                    {item.handler_code ? ` | ${formatHandlerCode(item.handler_code)}` : ""}
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
        </Box>
      </Box>
    )}

    {/* כפילויות */}
    {hasDuplicates && (
      <Box>
        <Divider sx={{ mb: 1.5 }} />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <WarningAmberIcon color="warning" fontSize="small" />
          <Typography variant="subtitle2" fontWeight="bold" color="warning.dark">כפילויות אפשריות</Typography>
        </Box>
        <Stack spacing={1}>
          {summary!.duplicate_warnings.map((w, i) => <DuplicateCard key={i} warning={w} />)}
        </Stack>
      </Box>
    )}

    {/* פעולות מומלצות */}
    {(summary?.next_recommended_actions?.length ?? 0) > 0 && (
      <Box>
        <Divider sx={{ mb: 1.5 }} />
        <Typography variant="subtitle2" fontWeight="bold" mb={0.5}>פעולות מומלצות</Typography>
        <Stack spacing={0.5}>
          {summary!.next_recommended_actions.map((a, i) => (
            <Typography key={i} variant="body2">• {a}</Typography>
          ))}
        </Stack>
      </Box>
    )}

  </Stack>
);

// ─── LayerSection ─────────────────────────────────────────────────────────────

const LayerSection: React.FC<{ group: HealthCoverageLayerGroup }> = ({ group }) => (
  <Box>
    <Typography variant="caption" fontWeight="bold" color="text.secondary"
      sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
      {group.label}
    </Typography>
    <Stack spacing={0.75} mt={0.75}>
      {group.rows.map((row) => <CategoryRow key={row.categoryKey} row={row} />)}
    </Stack>
  </Box>
);

// ─── CategoryRow ──────────────────────────────────────────────────────────────

const CategoryRow: React.FC<{ row: HealthCoverageUIRow }> = ({ row }) => {
  const riskLabel = formatDuplicateRisk(row.duplicateRisk);

  return (
    <Box sx={{
      px: 1.5, py: 1, borderRadius: 1,
      border: "1px solid",
      borderColor: "divider",
      bgcolor: row.layer === "CORE" ? "#f9f9f9" : "transparent",
    }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap", mb: 0.75 }}>
        <Typography sx={{ fontSize: 16, lineHeight: 1 }}>{row.icon}</Typography>
        <Typography variant="subtitle2" fontWeight={row.isCore ? "bold" : "600"}>
          {row.label}
        </Typography>
        <Chip
          label={row.statusLabel}
          size="small"
          color={row.statusColor}
          variant={row.overallStatus === "HAS" ? "outlined" : "filled"}
          sx={{ height: 20, fontSize: 11 }}
        />
        {riskLabel && (
          <Chip label={riskLabel} size="small" color="warning" variant="filled" sx={{ height: 20, fontSize: 11 }} />
        )}
        {row.derivedCoverage === "BASIC_EXTENDED" && (
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
            רמת כיסוי: בסיס + הרחבה
          </Typography>
        )}
      </Box>
      <SubCoverageGroups subCoverages={row.subCoverages} />
      {row.structureWarning === "SPLIT_COVERAGE" && row.structureWarningMessage && (
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, mt: 0.75, pr: 0.5 }}>
          <Typography sx={{ fontSize: 13, lineHeight: 1.4, flexShrink: 0 }}>⚠️</Typography>
          <Typography variant="caption" color="warning.dark">
            {row.structureWarningMessage}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// ─── SubCoverageGroups ────────────────────────────────────────────────────────

const SubCoverageGroups: React.FC<{ subCoverages: SubCoverage[] }> = ({ subCoverages }) => {
  const hasItems   = subCoverages.filter((s) => s.status === "HAS" && !s.is_optional);
  const extensions = subCoverages.filter((s) => s.status === "HAS" && s.is_optional);
  const missing    = subCoverages.filter((s) => s.status !== "HAS" && !s.is_optional);

  return (
    <Stack spacing={0.75} sx={{ pr: 1 }}>
      {hasItems.length > 0 && (
        <Stack spacing={0.4}>
          {hasItems.map((sub) => <SubCoverageRow key={sub.key} sub={sub} />)}
        </Stack>
      )}
      {extensions.length > 0 && (
        <Box>
          <Typography variant="caption" color="text.disabled" sx={{ display: "block", mb: 0.25 }}>הרחבות</Typography>
          <Stack spacing={0.4}>
            {extensions.map((sub) => <SubCoverageRow key={sub.key} sub={sub} />)}
          </Stack>
        </Box>
      )}
      {missing.length > 0 && (
        <Box>
          <Typography variant="caption" color="text.disabled" sx={{ display: "block", mb: 0.25 }}>חסר</Typography>
          <Stack spacing={0.4}>
            {missing.map((sub) => <SubCoverageRow key={sub.key} sub={sub} />)}
          </Stack>
        </Box>
      )}
    </Stack>
  );
};

// ─── SubCoverageRow ───────────────────────────────────────────────────────────

const SubCoverageRow: React.FC<{ sub: SubCoverage }> = ({ sub }) => {
  const riskLabel = formatDuplicateRisk(sub.duplicate_risk);

  const displayItems = sub.status === "HAS" && sub.items.length > 0
    ? [...new Map(sub.items.map((i) => [i.raw_value, i])).values()]
    : null;

  return (
    <Box>
      {displayItems ? (
        <Stack spacing={0.3}>
          {displayItems.map((item) => {
            const itemPolicies = sub.items
              .filter((i) => i.raw_value === item.raw_value && i.policy_number)
              .filter((v, i, a) => a.findIndex((x) => x.policy_number === v.policy_number) === i);
            return (
              <Box key={item.raw_value} sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                <Typography sx={{ fontSize: 13, lineHeight: 1, flexShrink: 0 }}>
                  {subCoverageIcon(sub.status, sub.importance)}
                </Typography>
                <Typography variant="caption" fontWeight={600} color={subCoverageTextColor(sub.status, sub.importance)}>
                  {formatCoverageItemLabel(item.raw_value) || item.raw_value}
                </Typography>
                {itemPolicies.length > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {itemPolicies.map((p, i) => (
                      <span key={p.policy_number} dir="ltr">
                        {i > 0 ? ", " : ""}פוליסה {p.policy_number}
                        {p.plan_classification ? ` ${formatPlanClassification(p.plan_classification as PlanClassification)}` : ""}
                      </span>
                    ))}
                  </Typography>
                )}
                {(() => {
                  const total = sub.items
                    .filter((i) => i.raw_value === item.raw_value)
                    .reduce((sum, i) => sum + (i.coverage_sum ?? 0), 0);
                  return total > 0 ? (
                    <Typography variant="caption" color="text.secondary">
                      סכום כיסוי: <strong>{formatCurrency(total)}</strong>
                    </Typography>
                  ) : null;
                })()}
                {riskLabel && (
                  <Typography variant="caption" color="warning.dark">
                    &nbsp;⚠ {riskLabel}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Stack>
      ) : (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
          <Typography sx={{ fontSize: 13, lineHeight: 1, flexShrink: 0 }}>
            {subCoverageIcon(sub.status, sub.importance)}
          </Typography>
          <Typography
            variant="caption" fontWeight={400}
            color={subCoverageTextColor(sub.status, sub.importance)}
            sx={{ fontStyle: sub.importance !== "CORE" && sub.duplicate_risk === "NONE" ? "italic" : "normal" }}
          >
            {sub.label}
          </Typography>
        </Box>
      )}
      {sub.next_step && (
        <Typography variant="caption"
          color={sub.status === "MISSING" && sub.duplicate_risk === "NONE" ? "text.disabled" : sub.duplicate_risk !== "NONE" ? "warning.dark" : "info.main"}
          sx={{ display: "block", pr: 2.5, mt: 0.2 }}>
          ← {sub.next_step}
        </Typography>
      )}
    </Box>
  );
};

// ─── DuplicateCard ────────────────────────────────────────────────────────────

const DuplicateCard: React.FC<{ warning: DuplicateWarning }> = ({ warning }) => (
  <Box sx={{ border: "1px solid", borderColor: "warning.light", borderRadius: 1, p: 1.25, bgcolor: "#fff8e1" }}>
    <Typography variant="body2" fontWeight="bold" gutterBottom>{warning.reason}</Typography>
    <Stack spacing={0.25} mb={0.75}>
      {warning.items.map((item, i) => (
        <Typography key={i} variant="caption" color="text.secondary">• {formatCoverageItemLabel(item)}</Typography>
      ))}
    </Stack>
    <Typography variant="caption" color="warning.dark">← {warning.recommendation}</Typography>
  </Box>
);

export default HealthCoverageAnalysisDialog;
